import {
  PROTOCOL_VERSION,
  encodeChunkFrame,
  encodeRelayFrame,
  type JsonMessage,
} from '../../../shared/protocol';
import type { FileMeta, Transfer, TransportPath } from './contract';
import type { RtcManager } from './rtc';
import type { ReliableSocket } from './socket';

export interface OutgoingRuntime {
  kind: 'send';
  files: File[];
  metas: FileMeta[];
  accepted: boolean;
  acked: number[];
  credits: number;
  creditWaiters: Array<() => void>;
  sending: boolean;
  lastPath: 'direct' | 'relay' | undefined;
  completeSent: boolean;
}

export interface OutgoingTransferContext {
  getTransfer(id: string): Transfer | undefined;
  setTransferPath(transferId: string, path: 'direct' | 'relay'): void;
  emit(): void;
  persist(): void;
  releaseWakeLock(): void;
  sendControl(peerId: string, message: JsonMessage): void;
  encryptFor(peerId: string, payload: Uint8Array): Promise<Uint8Array>;
  rtc: RtcManager;
  getSocket(): ReliableSocket | undefined;
  mode: 'relay' | 'webrtc' | 'auto';
  onTransferDone?(transferId: string): void;
}

export class OutgoingTransferManager {
  private readonly runtimes = new Map<string, OutgoingRuntime>();

  constructor(private readonly ctx: OutgoingTransferContext) {}

  register(transferId: string, runtime: OutgoingRuntime): void {
    this.runtimes.set(transferId, runtime);
  }

  get(transferId: string): OutgoingRuntime | undefined {
    return this.runtimes.get(transferId);
  }

  delete(transferId: string): boolean {
    return this.runtimes.delete(transferId);
  }

  handleDecision(transferId: string, accepted: boolean): void {
    const transfer = this.ctx.getTransfer(transferId);
    const runtime = this.runtimes.get(transferId);
    if (!transfer || !runtime) return;

    if (!accepted) {
      transfer.state = 'declined';
      transfer.finishedAt = Date.now();
      this.ctx.emit();
      this.ctx.persist();
      this.ctx.releaseWakeLock();
      return;
    }

    runtime.accepted = true;
    transfer.state = 'transferring';
    this.ctx.emit();

    if (this.ctx.mode === 'relay') {
      this.ctx.setTransferPath(transfer.id, 'relay');
    } else {
      void this.ctx.rtc.ensure(transfer.peerId, true);
      if (this.ctx.rtc.isOpen(transfer.peerId)) {
        this.ctx.setTransferPath(transfer.id, 'direct');
      } else {
        void this.waitForDirectOrRelay(transfer.id);
      }
    }
    this.pump(transfer.id);
  }

  private async waitForDirectOrRelay(transferId: string): Promise<void> {
    const transfer = this.ctx.getTransfer(transferId);
    if (!transfer) return;

    const direct = await this.ctx.rtc.waitOpen(transfer.peerId, 5_000);
    if (direct && this.ctx.mode !== 'relay') {
      this.ctx.setTransferPath(transferId, 'direct');
    } else if (this.ctx.mode === 'webrtc') {
      transfer.state = 'failed';
      transfer.error = 'WebRTC could not connect';
      transfer.finishedAt = Date.now();
      this.ctx.emit();
      this.ctx.persist();
      this.ctx.releaseWakeLock();
      return;
    } else {
      this.ctx.setTransferPath(transferId, 'relay');
    }
    this.pump(transferId);
  }

  handleAck(transferId: string, fileIndex: number, offset: number): void {
    const runtime = this.runtimes.get(transferId);
    const transfer = this.ctx.getTransfer(transferId);
    if (!runtime || !transfer) return;

    runtime.acked[fileIndex] = Math.max(runtime.acked[fileIndex], offset);
    transfer.bytesDone = runtime.acked.reduce((sum, off) => sum + off, 0);
    this.ctx.emit();
  }

  handleCredit(transferId: string, bytes: number): void {
    const runtime = this.runtimes.get(transferId);
    if (!runtime) return;

    runtime.credits += bytes;
    const waiters = runtime.creditWaiters.splice(0);
    for (const resolve of waiters) {
      resolve();
    }
  }

  async pump(transferId: string): Promise<void> {
    const transfer = this.ctx.getTransfer(transferId);
    const runtime = this.runtimes.get(transferId);
    if (
      !transfer ||
      !runtime ||
      !runtime.accepted ||
      runtime.sending ||
      transfer.state !== 'transferring'
    ) {
      return;
    }

    runtime.sending = true;
    try {
      for (let fileIndex = 0; fileIndex < runtime.files.length; fileIndex++) {
        const file = runtime.files[fileIndex];
        let offset = runtime.acked[fileIndex];

        while (offset < file.size && transfer.state === 'transferring') {
          const path = transfer.path;
          if (path !== 'direct' && path !== 'relay') {
            await new Promise((resolve) => window.setTimeout(resolve, 40));
            continue;
          }

          if (path === 'direct' && !this.ctx.rtc.isOpen(transfer.peerId)) {
            this.ctx.setTransferPath(transferId, this.ctx.mode === 'webrtc' ? 'direct' : 'relay');
            continue;
          }

          if (runtime.lastPath === 'direct' && path === 'relay') {
            offset = runtime.acked[fileIndex];
          }
          runtime.lastPath = path;

          const size = path === 'direct' ? 64 * 1024 : 256 * 1024;
          const bytes = new Uint8Array(
            await file.slice(offset, Math.min(file.size, offset + size)).arrayBuffer(),
          );
          if (!bytes.length) break;

          const frame = encodeChunkFrame({
            transferId,
            fileIndex,
            offset,
            payload: bytes,
          });

          if (path === 'direct') {
            await this.ctx.rtc.waitBuffer(transfer.peerId);
            if (!this.ctx.rtc.send(transfer.peerId, frame)) {
              this.ctx.setTransferPath(
                transferId,
                this.ctx.mode === 'webrtc' ? 'direct' : 'relay',
              );
              continue;
            }
          } else {
            while (runtime.credits < bytes.byteLength) {
              await new Promise<void>((resolve) => runtime.creditWaiters.push(resolve));
            }
            runtime.credits -= bytes.byteLength;
            const encrypted = await this.ctx.encryptFor(transfer.peerId, frame);
            this.ctx.getSocket()?.sendBinary(encodeRelayFrame(transfer.peerId, encrypted));
          }

          offset += bytes.byteLength;
          transfer.bytesDone = Math.max(
            transfer.bytesDone,
            runtime.acked.reduce((sum, v) => sum + v, 0) + Math.min(offset, file.size),
          );
          this.ctx.emit();
        }
      }

      if (transfer.state === 'transferring' && !runtime.completeSent) {
        runtime.completeSent = true;
        this.ctx.sendControl(transfer.peerId, {
          v: PROTOCOL_VERSION,
          type: 'transfer.complete',
          to: transfer.peerId,
          transferId,
        });
        transfer.state = 'done';
        transfer.finishedAt = Date.now();
        transfer.verified = true;
        transfer.bytesDone = transfer.totalBytes;
        this.ctx.emit();
        this.ctx.persist();
        this.ctx.releaseWakeLock();
        this.ctx.onTransferDone?.(transferId);
      }
    } finally {
      runtime.sending = false;
    }
  }
}
